import { useState } from "react";
import { LayoutDashboard, Calendar, HeartPulse, MoreHorizontal, Mic, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAssistantContext } from "@/contexts/AssistantContext";
import { PeritoMoreSheet } from "./PeritoMoreSheet";

interface DockItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  onClick?: () => void;
}

export function PeritoDock() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { togglePanel } = useAssistantContext();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (to?: string) =>
    !!to && (location.pathname === to || location.pathname.startsWith(to + "/"));

  const moreRoutes = ["/perito/seguimiento", "/perito/inventario", "/perito/perfil"];
  const moreActive = moreOpen || moreRoutes.some((r) => location.pathname.startsWith(r));

  const left: DockItem[] = [
    { key: "panel", label: t("perito.dock.panel"), icon: LayoutDashboard, to: "/perito/inicio" },
    { key: "planner", label: t("perito.dock.planner"), icon: Calendar, to: "/perito/planificador" },
  ];
  const right: DockItem[] = [
    { key: "sanidad", label: t("perito.dock.sanidad"), icon: HeartPulse, to: "/perito/sanidad" },
    { key: "more", label: t("perito.dock.more"), icon: MoreHorizontal, onClick: () => setMoreOpen((o) => !o) },
  ];

  const renderItem = (item: DockItem) => {
    const active = isActive(item.to) || (item.key === "more" && moreActive);
    // «Más» pasa a ser una X mientras el menú está abierto, igual que en el dock
    // de admin: el mismo botón que lo abre lo cierra, y se ve que lo hará.
    const Icon = item.key === "more" && moreOpen ? X : item.icon;
    return (
      <button
        key={item.key}
        onClick={() => (item.to ? navigate(item.to) : item.onClick?.())}
        className={cn(
          "flex flex-col items-center justify-center gap-[3px] flex-1 h-full rounded-2xl transition-colors",
          active ? "text-foreground" : "text-foreground/65"
        )}
      >
        <Icon className={cn("w-[22px] h-[22px]", active && "stroke-[2.5]")} />
        <span className={cn("text-[10px] font-medium leading-none", active && "font-semibold")}>{item.label}</span>
      </button>
    );
  };

  return (
    <>
      <div
        data-app-dock
        /* `lg:hidden`, como la barra del admin (`shell/MobileDock`): en una
           tableta en vertical (768-1023 px) la barra lateral todavía no existe
           (`useIsMobile` corta en 1024) y con `md:hidden` el perito se quedaba
           sin menú ni micrófono (auditoría T12-01 / T08-09). */
        className="lg:hidden fixed left-0 right-0 z-[45] pointer-events-none"
        style={{ bottom: "var(--dock-bottom)" }}
      >
        <div className="mx-auto max-w-md md:max-w-3xl px-[14px]">
          <div className="pointer-events-auto relative h-16 rounded-[24px] bg-white/[0.92] backdrop-blur-[16px] border border-[#E5E9F2] shadow-[0_12px_34px_rgba(20,60,100,0.16)] flex items-stretch px-2 md:px-3 md:gap-1.5">
            {left.map(renderItem)}
            <div className="w-16 flex-shrink-0" aria-hidden />
            {right.map(renderItem)}

            <button
              onClick={togglePanel}
              aria-label={t("perito.dock.voice")}
              className="absolute left-1/2 -translate-x-1/2 -top-[22px] w-[58px] h-[58px] rounded-full border-[3px] border-white shadow-[0_10px_24px_rgba(31,125,200,0.5)] flex items-center justify-center active:scale-95 transition-transform"
              style={{ background: "var(--cta)" }}
            >
              <Mic className="w-[25px] h-[25px] text-white" />
            </button>
          </div>
        </div>
      </div>

      <PeritoMoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </>
  );
}
